import { action, observable, runInAction } from "mobx";
import { TeamsEditForm } from "application/teams/forms";
import { BaseEditViewStore, BaasicDropdownStore } from "core/stores";
import { applicationContext } from "core/utils";
import { TeamsService } from "application/teams/services";
import { CoreUserService } from "application/user/services";
import _ from "lodash";

@applicationContext
class TeamsEditViewStore extends BaseEditViewStore {
    @observable accessor userIdsArray = [];
    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const service = new TeamsService(
            rootStore.application.baasic.apiClient
        );
        const coreUserService = new CoreUserService(
            rootStore.application.baasic.apiClient
        );

        super(rootStore, {
            name: "teams",
            id: id,
            Form: TeamsEditForm,
            authorization: "portalSection.update",
            actions: () => {
                return {
                    get: async (id, opts) => {
                        const response = await service.get(
                            id,
                            _.extend(opts, {
                                embed: "TeamUsers",
                            })
                        );
                        return response.data;
                    },
                    update: async (resource) => {
                        const response = await service.update({
                            id: resource.id,
                            name: resource.name,
                            userIds: resource.userIds,
                        });
                        return response.data;
                    },
                    create: async (resource) => {
                        const response = await service.create({
                            name: resource.name,
                            userIds: resource.userIds,
                        });
                        return response.data;
                    },
                };
            },
            FormClass: TeamsEditForm,
        });

        this.personService = service;

        this.userStore = new BaasicDropdownStore(
            {
                filterable: true,
                textField: "name",
                returnObject: true,
            },
            {
                fetchFunc: async (term) => {
                    let params = {
                        searchQuery: term,
                        sort: "firstName|asc",
                        rpp: 50,
                        embed: "CoreMembership",
                    };

                    const response = await coreUserService.find(params);

                    return response.data.item
                        .filter((user) => user.coreMembership.isApproved)
                        .map((u) => ({
                            id: u.id,
                            name: u.firstName + " " + u.lastName,
                        }));
                },
                onChange: action((selectedUser) => {
                    if (!selectedUser || !selectedUser.id) {
                        return;
                    }
                    if (
                        !this.userIdsArray.some(
                            (user) => user.id === selectedUser.id
                        )
                    ) {
                        this.userIdsArray.push({
                            id: selectedUser.id,
                            displayName: selectedUser.name,
                        });
                    }
                    this.userStore.filterTerm = "";
                }),
            }
        );
    }

    @action.bound
    async onInit({ initialLoad }) {
        if (!initialLoad) {
            this.rootStore.routerStore.goBack();
        } else if (this.id) {
         
            const team = await this.actions.get(this.id);
            runInAction(() => {
                this.userIdsArray = (team.teamUsers || []).map((u) => ({
                    id: u.userId,
                    displayName: u.firstName + " " + u.lastName,
                }));
            });
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.update({
                id: this.id,
                ...resource,
                userIds: this.userIdsArray.map((u) => u.id),
            });

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(
                () =>
                    this.rootStore.notificationStore.success(
                        "TEAMS.EDIT.SUCCESS"
                    ),
                10
            );
        } catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    @action.bound
    removeUserFromArray(id) {
        this.userIdsArray = this.userIdsArray.filter((user) => user.id !== id);
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.create({
                ...resource,
                userIds: this.userIdsArray.map((u) => u.id),
            });

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(() => this.notifySuccessCreate(), 10);
        } catch (err) {
            this.form.setFieldsDisabled(false);
            this.rootStore.notificationStore.error(
                err && err.data && err.data.errorMessage
            );
        }
    }

    @action.bound
    notifySuccessCreate() {
        this.rootStore.notificationStore.success("TEAMS.CREATE.SUCCESS");
    }
}

export default TeamsEditViewStore;
